import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { FunctionComponent, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { Icon } from '@iconify/react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { IFile } from '../models/IFile';
import { IGetListOfFilesResponse } from '../models/IGetListOfFilesResponse';
import { ISite } from '../models/ISite';
import {
  isInitializingAtom,
  pageSizeAtom,
  refinerItemsAtom,
  refinersAtom,
  searchKeywordAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  selectedSiteAtom,
  selectedStateAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { SharePointService } from '../utils/SharePointService';
import { FileInfoPanel } from './FileInfoPanel';
import FilePreview from './FilePreview';
import VideoPreview from './VideoPreview';

// Helper function to get the view icon
const getViewIconElement = (fileType: string) => {
  if (fileType === 'pdf') {
    return (
      <Icon
        icon='ant-design:file-pdf-filled'
        fr=''
        style={{ fontSize: '1.9rem' }}
      />
    );
  }
  return null;
};

// Helper function to get the download icon
const getDownloadIconElement = (fileType: string) => {
  if (fileType === '000') {
    return null;
  }
  return <DownloadIcon sx={{ fontSize: '1.9rem' }} />;
};

type FileTableProps = {
  useTemplateQuery: boolean;
  currentPageIndex: number;
  startRow: number;
  onSetCurrentPageIndex: (pageIndex: number) => void;
  onSetStartRow: (startRow: number) => void;
  isSourceOfRefiners?: boolean;
};

type FileRowProps = {
  file: IFile;
  selectedState: string | null;
  hitHighlightingEnabled: boolean | undefined;
  onAction: (file: IFile) => void;
  onPreview: (file: IFile) => void;
  onDownload: (file: IFile) => void;
  onInfo: (file: IFile) => void;
};

const FileRow: FunctionComponent<FileRowProps> = ({
  file,
  selectedState,
  hitHighlightingEnabled,
  onAction,
  onPreview,
  onDownload,
  onInfo,
}) => {
  return (
    <TableRow onDoubleClick={() => onAction(file)}>
      <TableCell>
        <Link
          onClick={() => onAction(file)}
          className='title'
          sx={{
            color: '#000000',
            textDecoration: 'none',
          }}
        >
          {file.fileName}
        </Link>
        {hitHighlightingEnabled && (
          <div
            style={{ fontSize: 13, color: '#878787' }}
            dangerouslySetInnerHTML={{
              __html: file.hitHightlights ? file.hitHightlights + '...' : '',
            }}
          ></div>
        )}
      </TableCell>
      <TableCell>
        <div style={{ fontSize: 13 }}>
          {(selectedState === 'QLD' ? file.qldLocation : file.nswLocation) ||
            '-'}
        </div>
      </TableCell>
      <TableCell sx={{ textAlign: 'center' }}>
        {file.isDownloadOnly ? (
          <Tooltip title={'You cannot preview ' + file.fileType}>
            <span>
              <IconButton
                onClick={() => onPreview(file)}
                disabled={file.isDownloadOnly || !file.previewUrl}
              >
                {getViewIconElement(file.fileType)}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton
            onClick={() => onPreview(file)}
            disabled={file.isDownloadOnly || !file.previewUrl}
          >
            {getViewIconElement(file.fileType)}
          </IconButton>
        )}
      </TableCell>
      <TableCell sx={{ textAlign: 'center' }}>
        <IconButton
          onClick={() => onDownload(file)}
          disabled={!file.downloadUrl}
        >
          {getDownloadIconElement(file.fileType)}
        </IconButton>
      </TableCell>
      <TableCell sx={{ textAlign: 'center' }}>
        <IconButton onClick={() => onInfo(file)}>
          <InfoIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const InitialMessage = () => (
  <TableRow>
    <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
      <ReportGmailerrorredIcon sx={{ color: '#6E6E6E' }} />
      <Typography sx={{ marginTop: '15px' }}>
        Please select a site from the left panel to display results.
      </Typography>
      <Typography sx={{ marginTop: '15px', marginBottom: '15px' }}>
        You can also change your location with any of the options on the top of
        the page.
      </Typography>
    </TableCell>
  </TableRow>
);

const NoResults = () => (
  <TableRow>
    <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
      0 matching results. Please try a different search query.
    </TableCell>
  </TableRow>
);

const LoadingState = () => (
  <TableRow>
    <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
      <CircularProgress size={20} />
    </TableCell>
  </TableRow>
);

type FileTableBodyProps = {
  isLocationSelected: boolean;
  isSiteSelected: boolean;
  isInitializing: boolean;
  itemsQuery: UseQueryResult<IGetListOfFilesResponse, unknown>;
  selectedState: string | null;
  selectedSite: ISite | null;
  onAction: (file: IFile) => void;
  onPreview: (file: IFile) => void;
  onDownload: (file: IFile) => void;
  onInfo: (file: IFile) => void;
};

const FileTableBody: FunctionComponent<FileTableBodyProps> = ({
  isLocationSelected,
  isSiteSelected,
  isInitializing,
  itemsQuery,
  selectedState,
  selectedSite,
  onAction,
  onPreview,
  onDownload,
  onInfo,
}) => {
  if (!isLocationSelected || !isSiteSelected) {
    return <InitialMessage />;
  }
  if (isInitializing && !itemsQuery.data) {
    return <LoadingState />;
  }
  if (itemsQuery.data?.files.length === 0) {
    return <NoResults />;
  }
  if (itemsQuery.data?.files) {
    return (
      <>
        {itemsQuery.data.files.map((file) => (
          <FileRow
            key={`${file.fileId}-${file.fileName}`}
            file={file}
            selectedState={selectedState}
            hitHighlightingEnabled={selectedSite?.HitHighliting}
            onAction={onAction}
            onPreview={onPreview}
            onDownload={onDownload}
            onInfo={onInfo}
          />
        ))}
      </>
    );
  }
  return null;
};

const MemoizedFileTableBody = React.memo(FileTableBody);

export const FileTable: FunctionComponent<FileTableProps> = (
  props: FileTableProps
) => {
  const sharepointService = SharePointService.Instance;
  const theme = useTheme();

  const selectedLocationId = useAtomValue(selectedLocationIdAtom);
  const selectedSite = useAtomValue(selectedSiteAtom);
  const selectedState = useAtomValue(selectedStateAtom);
  const searchKeyword = useAtomValue(searchKeywordAtom);
  const pageSize = useAtomValue(pageSizeAtom);
  const refiners = useAtomValue(refinersAtom);
  const selectedRefinerItems = useAtomValue(selectedRefinerItemsAtom);
  const setRefinerItems = useSetAtom(refinerItemsAtom);
  const [isInitializing, setIsInitializing] = useAtom(isInitializingAtom);

  const [selectedFile, setSelectedFile] = useState<IFile | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [infoFile, setInfoFile] = useState<IFile | null>(null);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowMessage, setIsShowMessage] = useState(false);
  const [paginationStatus, setPaginationStatus] = useState<
    'fetching prev' | 'fetching next' | 'idle'
  >('idle');

  const itemsQuery = useQuery(
    [
      Constants.QUERY_KEYS.FILES,
      selectedLocationId,
      selectedSite,
      searchKeyword,
      pageSize,
      props.startRow,
      selectedRefinerItems,
      props.useTemplateQuery,
    ],
    () =>
      sharepointService.getListOfFiles(
        selectedLocationId,
        selectedSite ? selectedSite.Id : 0,
        searchKeyword,
        pageSize,
        props.startRow,
        selectedRefinerItems,
        refiners.map((refiner) => refiner.property),
        props.useTemplateQuery
      ),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      enabled:
        selectedLocationId !== Constants.DEFAULT_ID && selectedSite !== null,
      keepPreviousData: true,
    }
  );

  React.useEffect(() => {
    if (itemsQuery.data) {
      setIsInitializing(false);
      if (props.isSourceOfRefiners) {
        setRefinerItems(itemsQuery.data.refiners);
      }
    }
  }, [
    itemsQuery.data,
    setRefinerItems,
    setIsInitializing,
    props.isSourceOfRefiners,
  ]);

  React.useEffect(() => {
    if (!itemsQuery.isFetching) {
      setPaginationStatus('idle');
    }
  }, [itemsQuery.isFetching]);

  function hidePreview() {
    setShowPreview(false);
    setShowVideoPreview(false);
    setSelectedFile(null);
  }

  function hideInfoPanel() {
    setInfoFile(null);
    setShowInfoPanel(false);
  }

  function downloadAction(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop() || '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function downloadFile(file: IFile) {
    if (file.fileType === '000') {
      return;
    }

    setIsLoading(true);
    downloadAction(file.downloadUrl);
    // This timeout is a workaround to show a loading indicator,
    // as there's no direct feedback from the download initiation.
    setTimeout(() => {
      setIsLoading(false);
      setIsShowMessage(true);
    }, 1000);
  }

  function previewFile(file: IFile) {
    const isPdf = file.fileType === 'pdf';
    if (!isPdf) {
      return;
    }

    if (
      window.matchMedia(theme.breakpoints.down('sm').replace('@media ', ''))
        .matches
    ) {
      window.open(file.previewUrl);
      return;
    }

    setSelectedFile(file);
    setShowPreview(true);
  }

  function handleActionItem(file: IFile) {
    return file.isDownloadOnly || !file.previewUrl
      ? downloadFile(file)
      : previewFile(file);
  }

  function showFileInfo(file: IFile) {
    setInfoFile(file);
    setShowInfoPanel(true);
  }

  function openPageResult(nextPage: number) {
    const _startRow = (nextPage - 1) * pageSize;
    props.onSetCurrentPageIndex(nextPage);
    props.onSetStartRow(_startRow);
  }

  const isLocationSelected = selectedLocationId !== Constants.DEFAULT_ID;
  const isSiteSelected = selectedSite !== null;

  return (
    <>
      <TableContainer component={Paper} elevation={3} sx={{ marginTop: 1 }}>
        <Table
          sx={{
            minWidth: 650,
            '& td': { verticalAlign: 'top' },
            '& strong': { color: '#626262' },
          }}
          aria-label='search result'
        >
          <TableHead>
            {isLocationSelected && isSiteSelected && (
              <TableRow
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '& th': {
                    color: '#ffffff',
                    padding: 1,
                  },
                }}
              >
                <TableCell sx={{ minWidth: 250 }}>Title</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Location</TableCell>
                <TableCell sx={{ width: 80 }}>View</TableCell>
                <TableCell sx={{ width: 110 }}>Download</TableCell>
                <TableCell sx={{ width: 80 }}>Info</TableCell>
              </TableRow>
            )}
          </TableHead>
          <TableBody
            sx={{
              '& tr:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: 'pointer',
                '& .title': {
                  textDecoration: 'underline',
                  color: '#0056b3',
                },
              },
              '& td': {
                padding: 1,
                '& button': {
                  padding: 0,
                  '& svg': {
                    fontSize: '1.5rem',
                  },
                },
              },
            }}
          >
            <MemoizedFileTableBody
              isLocationSelected={isLocationSelected}
              isSiteSelected={isSiteSelected}
              isInitializing={isInitializing}
              itemsQuery={itemsQuery}
              selectedState={selectedState}
              selectedSite={selectedSite}
              onAction={handleActionItem}
              onPreview={previewFile}
              onDownload={downloadFile}
              onInfo={showFileInfo}
            />
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ marginTop: 2, textAlign: 'center' }}>
        <ButtonGroup variant='outlined'>
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            disabled={itemsQuery.isFetching || props.currentPageIndex === 1}
            onClick={() => {
              setPaginationStatus('fetching prev');
              openPageResult(props.currentPageIndex - 1);
            }}
          >
            <Box
              sx={{ minWidth: 69, display: 'flex', justifyContent: 'center' }}
            >
              {paginationStatus === 'fetching prev' && itemsQuery.isFetching ? (
                <CircularProgress sx={{ color: '#00000042' }} size={16} />
              ) : (
                'Previous'
              )}
            </Box>
          </Button>
          <Button
            endIcon={<ArrowForwardIosIcon />}
            disabled={
              isInitializing ||
              itemsQuery.isFetching ||
              itemsQuery.data?.lastPage
            }
            onClick={() => {
              setPaginationStatus('fetching next');
              openPageResult(props.currentPageIndex + 1);
            }}
          >
            <Box
              sx={{ minWidth: 37, display: 'flex', justifyContent: 'center' }}
            >
              {paginationStatus === 'fetching next' && itemsQuery.isFetching ? (
                <CircularProgress sx={{ color: '#00000042' }} size={16} />
              ) : (
                'Next'
              )}
            </Box>
          </Button>
        </ButtonGroup>
      </Box>

      {/* File preview dialog */}
      {selectedFile && (
        <>
          <FilePreview
            title={selectedFile.fileName}
            show={showPreview}
            fileSource={selectedFile.previewUrl}
            onClose={hidePreview}
          ></FilePreview>
          <VideoPreview
            show={showVideoPreview}
            fileSource={selectedFile.previewUrl}
            title={selectedFile.fileName}
            onClose={hidePreview}
          ></VideoPreview>
        </>
      )}

      {/* File info panel */}
      {showInfoPanel && infoFile && (
        <FileInfoPanel file={infoFile} onClose={hideInfoPanel} />
      )}

      {/* Download backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>

      {/* Download message */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={isShowMessage}
        autoHideDuration={3000}
        onClose={() => setIsShowMessage(false)}
        message={'File has been requested. Please wait for a few seconds.'}
      />
    </>
  );
};
